<template>
  <div class="profile-section">
    <m-confirm ref="confirm" mode="danger" />
    <m-profile-tab
      ref="tabModal"
      :profile-id="profileId"
      :profile-section="profileSection"
      @created="onTabCreated"
      @updated="onTabUpdated"
    />
    <m-profile-field
      ref="fieldModal"
      :tabs="tabs"
      :fields="sectionFields"
      :profile-id="profileId"
      @created="onFieldCreated"
      @updated="onFieldUpdated"
    />
    <m-move-field ref="moveFieldModal" :tabs="tabs" @updated="onFieldMoved" />

    <div class="profile-section__actions">
      <c-button-dropdown
        class="profile-section__copy"
        icon="arrow"
        type="dropdown"
        label="Скопировать анкету"
        :dropdown="{
          position: 'left',
          items: otherProfileSections,
        }"
        @copy="copyProfile"
      />

      <c-button
        class="profile-section__add-field"
        size="small"
        :icon="{ icon: 'plus', color: 'primary' }"
        label="Добавить поле"
        :disabled="!tabs.length"
        @click="createField"
      />
      <c-button
        class="profile-section__add-tab"
        size="small"
        :icon="{ icon: 'plus', color: 'primary' }"
        label="Добавить вкладку"
        @click="createTab"
      />
    </div>

    <draggable
      class="profile-section__tabs"
      :group="`profile-section-${_uid}`"
      :list="tabs"
      :force-fallback="true"
      ghost-class="tab-ghost"
      @change="resortTabs"
    >
      <div
        v-for="tab in tabs"
        :key="tab.id"
        class="profile-tab"
        :class="{
          'profile-tab--opened': tab.opened,
        }"
      >
        <observer class="profile-tab__header" :disabled="true">
          <toggler-button :open="tab.opened" :disabled="!tab.fields.length" @click="tab.opened = !tab.opened" />
          <div class="profile-tab__name">
            {{ tab.name }}
            <span v-if="!tab.fields.length" class="empty">(пусто)</span>
          </div>

          <div class="profile-tab__actions">
            <c-button
              v-b-popover.html="{
                container: '#app',
                trigger: 'hover',
                content: 'Редактировать',
                placement: 'bottom',
              }"
              class="profile-tab__edit"
              icon="edit"
              size="iconic"
              @click="editTab(tab)"
            />
            <c-button
              v-b-popover.html="{
                container: '#app',
                trigger: 'hover',
                content: 'Удалить',
                placement: 'bottom',
              }"
              class="profile-tab__remove"
              :icon="{ icon: 'trash', color: 'secondary' }"
              size="iconic"
              @click="deleteTab(tab)"
            />
            <c-button
              v-b-popover.html="{
                container: '#app',
                trigger: 'hover blur',
                boundary: 'window',
                content: 'Переместить',
                placement: 'top',
              }"
              class="profile-tab__move"
              size="iconic"
              icon="arrows-constructor"
            />
          </div>
        </observer>

        <transition name="slide">
          <div v-if="tab.opened" class="profile-tab__fields">
            <profile-tab
              :fields="tab.fields"
              @editField="editField($event, tab.id)"
              @moveField="moveField($event, tab.id)"
              @removeField="deleteField($event, tab.id)"
            />
          </div>
        </transition>
      </div>
    </draggable>

    <div
      v-if="tabs.length"
      class="profile-section__footer"
      :class="{
        fixed: fixFooter,
      }"
    >
      <c-button
        class="profile-section__add-field"
        size="small"
        :icon="{ icon: 'plus', color: 'primary' }"
        label="Добавить поле"
        :disabled="!tabs.length"
        @click="createField"
      />
      <c-button
        class="profile-section__add-tab"
        size="small"
        :icon="{ icon: 'plus', color: 'primary' }"
        label="Добавить вкладку"
        @click="createTab"
      />
      <observer class="footer-observer" @fullyVisible="fixFooter = false" @fullyHidden="fixFooter = true" />
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";

import CButton from "@/components/controls2/Button";
import CButtonDropdown from "@/components/controls/button-dropdown";
import TogglerButton from "@/components/toggler-button";
import MConfirm from "@/components/modals/confirm-modal";
import Observer from "@/components/IntersectionObserver";

import ProfileTab from "./ProfileTab";
import MProfileTab from "../modals/ProfileTabModal";
import MProfileField from "../modals/ProfileFieldModal";
import MMoveField from "../modals/MoveFieldModal";
import {
  PROFILE_SECTION_CANDIDATE,
  PROFILE_SECTION_CANDIDATE_SHORT,
  PROFILE_SECTION_EMPLOYEE,
} from "@/domains/profile/constants/profile-sections";

const PROFILE_SECTIONS = [
  { label: "Анкету&nbsp;сотрудника", value: PROFILE_SECTION_EMPLOYEE, emit: "copy" },
  { label: "Анкету&nbsp;кандидата", value: PROFILE_SECTION_CANDIDATE, emit: "copy" },
  { label: "Краткую&nbsp;анкету&nbsp;кандидата", value: PROFILE_SECTION_CANDIDATE_SHORT, emit: "copy" },
];

export default {
  components: {
    draggable,
    CButton,
    CButtonDropdown,
    TogglerButton,
    MConfirm,
    ProfileTab,
    Observer,
    MProfileTab,
    MProfileField,
    MMoveField,
  },
  props: ["profileId", "profileSection", "profileTabs"],
  data() {
    return {
      fixFooter: false,
    };
  },

  computed: {
    otherProfileSections() {
      return PROFILE_SECTIONS.filter((section) => section.value !== this.profileSection);
    },
    tabs() {
      const section = `${this.profileSection}`;
      const sectionTabs = this.profileTabs.filter((tab) => tab.profileSection === section);
      return sectionTabs;
    },

    fields() {
      let result = [];
      this.profileTabs.forEach((tab) => {
        result = [...result, ...tab.fields];
      });
      return result;
    },
    sectionFields() {
      let result = [];
      this.tabs.forEach((tab) => {
        result = [...result, ...tab.fields];
      });
      return result;
    },
  },

  watch: {
    fixFooter(v) {
      this.$emit("fixFooter", v);
    },
  },

  methods: {
    /** TABS */
    createTab() {
      this.$refs.tabModal.open();
    },
    editTab(tab) {
      this.$refs.tabModal.open(tab);
    },
    deleteTab(tab) {
      const action = () => {
        this.$emit("tabRemoved", { tabId: tab.id });
        this.$store.dispatch("profile/deleteTab", tab.id).then(() => {
          this.$notify({
            group: "success-message",
            data: {
              textMessage: `Вкладка <b>${tab.name}</b> успешно удалена`,
            },
          });
        });
      };

      if (tab.fields.length) {
        this.$refs.confirm.open({
          title: "Удаление вкладки",
          text: "Все поля в этой вкладке будут также удалены",
          confirm: "Удалить",
          onConfirm: () => {
            action();
          },
        });
      } else {
        action();
      }
    },
    onTabCreated(tab) {
      this.$emit("tabCreated", { tabData: tab });
    },
    onTabUpdated(tab) {
      this.$emit("tabUpdated", { tabData: tab });
    },
    resortTabs() {
      const order = this.tabs.map((t) => t.id);
      this.$emit("tabsResorted", order);
    },

    /** FIELDS */
    createField() {
      this.$refs.fieldModal.open();
    },

    editField(field, tabId) {
      this.$refs.fieldModal.open(field, tabId);
    },
    moveField(field, tabId) {
      this.$refs.moveFieldModal.open(field, tabId);
    },
    deleteField(field, tabId) {
      this.$store.dispatch("profile/deleteField", field.id).then(() => {
        this.$notify({
          group: "success-message",
          data: {
            textMessage: `Параметр <b>${field.name}</b> успешно удален`,
          },
        });
      });
      this.$emit("fieldRemoved", { fieldId: field.id, tabId });
    },

    onFieldCreated({ field: fieldData, tabId }) {
      this.$emit("fieldCreated", { tabId, fieldData });
    },
    onFieldUpdated({ field: fieldData, tabId, oldTabId }) {
      this.$emit("fieldUpdated", { tabId, fieldData, oldTabId });
    },
    onFieldMoved({ field, oldTabId, newTabId }) {
      this.$emit("fieldMoved", { field, oldTabId, newTabId });
    },
    /** OTHER */
    copyProfile(from) {
      this.$emit("copySection", { from, to: this.profileSection });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";
@import "~@/assets/styles/decoration";
.profile-section {
  padding-bottom: 20px;

  &__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 25px;
    margin-bottom: 20px;
  }
  &__tabs {
    margin-bottom: 6px;
  }
  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-top: 14px;
    padding-bottom: 14px;

    position: sticky;
    bottom: -30px;

    &.fixed {
      background: white;
      z-index: 10;
      &:before {
        content: "";
        position: absolute;
        left: -30px;
        right: -40px;
        top: 0;
        bottom: 0;
        background: white;
        z-index: 0;
        box-shadow: 0px 0px 15px rgba(0, 21, 80, 0.3);
      }
    }

    .footer-observer {
      bottom: -1px;
      position: absolute;
    }

    /deep/ {
      .c-button {
        position: relative;
        z-index: 1;
      }
    }
  }
  &__add-field {
    margin-right: 10px;
  }
  &__copy {
    margin-right: 10px;

    /deep/ {
      .c-button__dropdown__container {
        max-width: 300px;
      }
    }
  }

  @include beforeBootstrapLg {
    padding-bottom: 70px;

    &__footer {
      bottom: 20px;
    }
  }

  @include beforeBootstrapMd {
    padding-bottom: 30px;
    &__actions {
      flex-direction: column;
      align-items: flex-start;
    }
    &__footer {
      flex-direction: column;
      align-items: flex-start;

      position: relative;
      bottom: 0;
    }
    &__add-field,
    &__copy {
      margin-right: 0;
      margin-bottom: 12px;
    }
  }
}
.profile-tab {
  &:last-child {
    .profile-tab__fields {
      border-bottom: 1px solid #dfe4ef;
    }
  }
  &__header {
    min-height: 48px;
    width: 100%;
    display: flex;
    align-items: center;
    padding: 5px 15px;
    background: #f5f7fa;
    border: 1px solid #dfe4ef;
    position: sticky;
    top: -34px;
    z-index: 1;
  }

  &__name {
    font-size: 15px;
    font-weight: 500;
    margin-left: 15px;

    .empty {
      font-size: 11px;
      color: $color-label;
      font-weight: normal;
    }
  }

  &__actions {
    margin-left: auto;
    padding-left: 20px;
    display: flex;
    transform: translateX(8px);
  }

  &__edit,
  &__remove {
    margin-right: 15px;
  }

  &__remove {
    transform: translateY(-3px);
  }

  &__fields {
    background: #f5f7fa;
  }

  &:not(:first-child) {
    margin-top: -1px;
  }

  @include beforeBootstrapLg {
    &__header {
      position: relative;
      top: 0;
    }
  }

  @include beforeBootstrapMd {
    margin-left: -20px;
    margin-right: -20px;
  }
}
/deep/ {
  .tab-ghost {
    @include dragGhost;
    position: relative;
    width: 100% !important;
  }
  .sortable-drag {
    @include draggingElement;
    .profile-tab__move svg path {
      fill: $color-primary;
    }
  }
}
</style>
